import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Download, Plus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Kpi, Money, Loading, Empty, StatusBadge, Field } from "@/components/kit";
import { todayIso, dmy } from "@/lib/format";

export default function Dashboard() {
  const { api, t, company, can, download } = useApp();
  const [range, setRange] = useState({ from: `${todayIso().slice(0, 4)}-01-01`, to: todayIso() });
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard", { params: range }).then((r) => setD(r.data)); }, [api, range]);
  const sub = company.subscription || {};
  return (
    <>
      <PageHeader eyebrow={company.name} title={t("dashboard")}>
        <Field label={t("from")}><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="dash-from" className="h-9" /></Field>
        <Field label={t("to")}><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="dash-to" className="h-9" /></Field>
        <Button variant="outline" className="self-end" data-testid="dash-export-xlsx" onClick={() => download(`/dashboard/export?format=xlsx&from=${range.from}&to=${range.to}`, "tableau_de_bord.xlsx")}><Download className="h-4 w-4 me-1" />{t("export_xlsx")}</Button>
        <Button variant="outline" className="self-end" data-testid="dash-export-pdf" onClick={() => download(`/dashboard/export?format=pdf&from=${range.from}&to=${range.to}`, "tableau_de_bord.pdf", false)}><Download className="h-4 w-4 me-1" />{t("export_pdf")}</Button>
        {can("sales", true) && <Button asChild className="self-end" data-testid="dash-new-invoice"><Link to="/app/documents/new?type=invoice"><Plus className="h-4 w-4 me-1" />{t("type_invoice")}</Link></Button>}
      </PageHeader>
      {sub.status === "trial" && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="trial-banner">
          {t("trial")} · {t("expires")} {dmy(sub.expires_at)} · {t("invoices_sent")} : {sub.invoices_sent || 0} / {company.plan?.trial_ttn_quota}
          {can("subscription") && <Link to="/app/subscription" className="ms-auto font-semibold underline">{t("subscription")}</Link>}
        </div>
      )}
      {!d ? <Loading /> : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi testid="kpi-revenue" label={t("revenue")} value={<Money v={d.revenue} />} />
            <Kpi testid="kpi-expenses" label={t("expenses")} value={<Money v={d.purchases} />} accent="navy" />
            <Kpi testid="kpi-margin" label={t("margin")} value={<Money v={d.margin} />} accent="orange" />
            <Kpi testid="kpi-receivable" label={t("receivables")} value={<Money v={d.receivable} />} accent="red"
              sub={<>{t("overdue")} : {d.overdue_count} · <Money v={d.overdue_amount} /></>} />
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <Panel title={t("monthly_chart")} className="lg:col-span-2" testid="chart-monthly">
              {d.by_month.length ? (
                <div className="h-72" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={d.by_month}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={70} />
                      <Tooltip formatter={(v) => v.toFixed(3)} /><Legend />
                      <Bar dataKey="revenue" name={t("revenue")} fill="#E30613" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="purchases" name={t("expenses")} fill="#111418" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <Empty />}
            </Panel>
            <Panel title={t("einv_status")} testid="einv-status-panel">
              <div className="space-y-2.5">
                {Object.keys(d.einv_status).length === 0 && <Empty />}
                {Object.entries(d.einv_status).map(([s, n]) => (
                  <div key={s} className="flex items-center justify-between"><StatusBadge status={s} /><span className="num font-semibold">{n}</span></div>
                ))}
              </div>
            </Panel>
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            {[["top_clients", d.top_clients], ["top_items", d.top_items]].map(([k, rows]) => (
              <Panel key={k} title={t(k)} testid={`panel-${k}`}>
                {rows.length ? rows.map((r) => (
                  <div key={r.name} className="flex items-center justify-between border-b border-slate-50 py-2 text-sm last:border-0">
                    <span className="truncate pe-3">{r.name}</span><Money v={r.value} className="font-medium" />
                  </div>
                )) : <Empty />}
              </Panel>
            ))}
            <Panel title={t("low_stock")} testid="panel-low-stock">
              {d.low_stock.length ? d.low_stock.map((i) => (
                <div key={i.code} className="flex items-center gap-2 py-1.5 text-sm"><AlertTriangle className="h-4 w-4 text-orange-700" />
                  <span className="truncate">{i.code} · {i.name_fr}</span><span className="num ms-auto">{i.stock_total} / {i.min_stock}</span></div>
              )) : <Empty />}
              {d.portfolio.length > 0 && (
                <div className="mt-4 border-t pt-3"><div className="eyebrow mb-2">{t("portfolio")}</div>
                  {d.portfolio.slice(0, 5).map((p) => (
                    <div key={p.id} className="flex justify-between text-sm py-1"><span>{p.partner_name} · {dmy(p.due_date)}</span><Money v={p.net_amount} /></div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </>
      )}
    </>
  );
}
